/**
 * D6SlotRenderer - Rendu personnalisé des slots pour le système D6
 * Affiche les icônes pour les extraFields (PV, Force, Dégâts)
 */

import React from 'react';
import {View, Text, StyleSheet} from 'react-native';
import {FontAwesomeIcon} from '@fortawesome/react-native-fontawesome';
import {faDumbbell, faHeart, faBolt} from '@fortawesome/free-solid-svg-icons';
import {GameState} from '../../models/GameState';
import {GameDataExtraField} from '../../models/GameDataExtraField';
import GameRegistry from '../GameRegistry';
import {Platform} from 'react-native';

interface D6SlotRendererProps {
  gameState: GameState | null;
  slotIndex: number;
}

const D6SlotRenderer: React.FC<D6SlotRendererProps> = ({gameState}) => {
  if (!gameState) {
    return null;
  }

  const extraFields: GameDataExtraField[] = gameState.data.adventureExtraFields || [];

  // Mapper les types d'extraFields D6 vers leurs icônes et labels
  const getExtraFieldInfo = (field: GameDataExtraField) => {
    const extraFieldType = GameRegistry.getExtraFieldType(field.type);
    if (!extraFieldType) {
      return null;
    }

    switch (field.type) {
      case 'D6_FORCE':
        return {
          icon: faDumbbell,
          label: 'Force',
          color: '#4A90E2',
        };
      case 'D6_PV':
        return {
          icon: faHeart,
          label: 'PV',
          color: '#E74C3C',
        };
      case 'D6_DAMAGE':
        return {
          icon: faBolt,
          label: 'Dégâts',
          color: '#F39C12',
        };
      default:
        return null;
    }
  };

  if (extraFields.length === 0) {
    return null;
  }

  return (
    <View style={styles.container}>
      {extraFields.map((field, idx) => {
        const info = getExtraFieldInfo(field);
        if (!info) {
          return null;
        }

        // Pour D6, afficher TOUJOURS la currentValue (valeur actuelle de la partie)
        // Si currentValue existe (même "0"), l'utiliser, sinon fallback sur initValue
        let value: string;
        if (field.currentValue !== undefined && field.currentValue !== null) {
          value = String(field.currentValue);
        } else if (field.initValue !== undefined && field.initValue !== null) {
          value = String(field.initValue);
        } else {
          value = '';
        }

        // Debug: log pour vérifier les valeurs
        console.log(`[D6SlotRenderer] Field ${field.type}: currentValue="${field.currentValue}" (type: ${typeof field.currentValue}), initValue="${field.initValue}" (type: ${typeof field.initValue}), final value="${value}"`);

        return (
          <View key={idx} style={styles.extraFieldItem}>
            <FontAwesomeIcon icon={info.icon} size={16} color={info.color} />
            <Text style={styles.extraFieldLabel}>{info.label}</Text>
            <Text style={[styles.extraFieldValue, {color: info.color}]}>{value}</Text>
          </View>
        );
      })}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 8,
    gap: 12,
  },
  extraFieldItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    ...Platform.select({
      web: {
        borderTopLeftRadius: 6,
        borderTopRightRadius: 4,
        borderBottomRightRadius: 6,
        borderBottomLeftRadius: 4,
      },
      default: {
        borderRadius: 6,
      },
    }),
  },
  extraFieldLabel: {
    fontSize: 12,
    color: '#4A5568',
    fontWeight: '500',
  },
  extraFieldValue: {
    fontSize: 13,
    fontWeight: '600',
  },
});

export default D6SlotRenderer;
